import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";

import {
  SettingsSectionWrapper1,
  SettingsSectionWrapper2,
} from "../../pageComponents/settings/style";
import {
  PlanDescription,
  PlanDetailWrapper,
  PlanDetailsWrapper,
  PlanName,
  PlanPrice,
  Wrapper,
} from "../planDetailsSection/style";

import {
  isLoadingSelector,
  paymentLogsListSelector,
  purchaseSeatLogsListSelector,
  purchaseSeatLogspaginationParamsSelector,
} from "../../store/payment/selector";
import { PaymentSagaActions } from "../../store/payment/sagas";
import { PaymentReducerAction } from "../../store/payment/slice";
import { paymentLogsListDataType } from "../../store/payment/types";
import {NavigatePrevIcon} from "../../assets/Icons";
import {AdDrawerStyledButton} from "../commonComponents/adDetails/style";
import {useNavigate} from "react-router-dom";

const PurchaseSeatLogs = () => {
  const dispatch = useDispatch();
  const purchaseSeatLogsList = useSelector(purchaseSeatLogsListSelector);
  const paginationParams = useSelector(
    purchaseSeatLogspaginationParamsSelector
  );
  const isLoading = useSelector(isLoadingSelector);
  const navigate = useNavigate();
  const loaderRef = useRef<HTMLDivElement | null>(null);

  // Initial Fetch
  useEffect(() => {
    dispatch(PaymentSagaActions.getPurchaseSeatLogsList(paginationParams));
  }, [dispatch]);

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          !isLoading &&
          purchaseSeatLogsList.data.length < purchaseSeatLogsList.total
        ) {
          dispatch(
            PaymentReducerAction.setPurchaseSeatLogspaginationParams({
              skip: purchaseSeatLogsList.data.length,
              limit: paginationParams.limit,
            })
          );
          dispatch(
            PaymentSagaActions.getPurchaseSeatLogsList(paginationParams)
          );
        }
      },
      { threshold: 1 }
    );

    const currentElement = loaderRef.current;
    if (currentElement) observer.observe(currentElement);

    return () => {
      if (currentElement) observer.unobserve(currentElement);
    };
  }, [dispatch, isLoading, paginationParams]);

  function capitalizeString(input: string): string {
    return input
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  function renderLabelAndValue(payment?: {
    status?: string;
    updatedAt?: string;
  }) {
    if (!payment) return null;

    const updatedDate = payment.updatedAt
      ? new Date(payment.updatedAt).toLocaleDateString()
      : "";

    switch (payment.status) {
      case "CANCEL_REQUEST":
        return <>Seat Cancelled on {updatedDate}</>;

      case "SEAT_REMOVE":
        return <>Seat Removed on {updatedDate}</>;

      case "SEAT_REEABLE":
        return <>Seat Released on {updatedDate}</>;

      case "SEAT_DESABLE":
        return <>Seat Disabled on {updatedDate}</>;

      case "SEAT_ASSIGN":
        return <>Seat Assigned on {updatedDate}</>;

      case "PURCHASE_SEATS":
        return <>Seat Purchased on {updatedDate}</>;

      case "UPGRADE_PLAN_SEATS":
        return <>Seat Upgraded on {updatedDate}</>;
   
      case "RECURRING_PLAN_SEATS":
        return <>Seat Renew on {updatedDate}</>;  

      default:
        return <>Unknown status on {updatedDate}</>;
    }
  }

  return (
    <SettingsSectionWrapper1>
      <AdDrawerStyledButton onClick={() => navigate(-1)}>
        <NavigatePrevIcon />
        Back
      </AdDrawerStyledButton>
      {isLoading && purchaseSeatLogsList.data.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <SettingsSectionWrapper2>
          {purchaseSeatLogsList?.data?.length ? (
            purchaseSeatLogsList.data.map((payment: any) => (
              <Wrapper key={payment._id}>
                <PlanDetailsWrapper>
                  <PlanDetailWrapper>
                    {payment?.status && (
                      <Chip
                        label={capitalizeString(payment?.status)}
                        sx={{
                          padding: "3px 7px",
                          borderRadius: "20px",
                          background: "#FAFAFA",
                          border:
                            payment?.status === "CANCEL_REQUEST"
                              ? "1px solid red"
                              : payment?.status === "PURCHSE_PLAN"
                              ? "1px solid green"
                              : "1px solid #1976d2",
                          color:
                            payment?.status === "CANCEL_REQUEST"
                              ? "red"
                              : payment?.status === "PURCHSE_PLAN"
                              ? "green"
                              : "#1976d2",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      />
                    )}
                    <PlanName>{payment?.memberSeatEmail}</PlanName>
                    {/* Price Info */}
                    <PlanDescription>
                      {renderLabelAndValue(payment)}
                    </PlanDescription>
                    {/* {payment?.amount ? ( */}
                      <PlanPrice weight={400} size="0.875rem">
                        Charged amount:{" "}
                        <PlanPrice weight={700} size="1rem">
                          ${payment?.amount ?? 0}
                        </PlanPrice>
                      </PlanPrice>
                    {/* ) : null} */}
                  </PlanDetailWrapper>
                </PlanDetailsWrapper>
              </Wrapper>
            ))
          ) : (
            <Box
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Looks like you haven’t made a payment yet!
              </Typography>
            </Box>
          )}

          {/* Infinite Scroll Trigger */}
          <Box
            ref={loaderRef}
            sx={{ height: "50px", textAlign: "center", mt: 2 }}
          >
            {isLoading && <CircularProgress size={24} />}
          </Box>
        </SettingsSectionWrapper2>
      )}
    </SettingsSectionWrapper1>
  );
};

export default PurchaseSeatLogs;
